import { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, apiPatch, apiDelete } from "../lib/api";
import Masthead from "../components/Masthead";
import ConnectorDialog from "../components/connectors/ConnectorDialog";
import EventsDialog from "../components/connectors/EventsDialog";
import { Button } from "../components/ui/button";
import { Plug, Pencil, Trash2, RefreshCw, PlugZap, CheckCircle2, XCircle, Clock, History, ShieldCheck, AlertTriangle, Timer, Search, X } from "lucide-react";

const STATUS_UI = {
  connected: { bg: "rgba(62,124,79,.12)", fg: "#2F6B41", Icon: CheckCircle2, label: "Connected" },
  error: { bg: "rgba(165,48,15,.10)", fg: "#A5300F", Icon: XCircle, label: "Error" },
  untested: { bg: "var(--cv-sand)", fg: "var(--cv-dark-grey)", Icon: Clock, label: "Untested" },
};

// Distinct accent per category so the store reads as a colourful marketplace.
const CATEGORY_THEME = {
  edr: "#B4471F", firewall: "#2F6B41", swg: "#2563A6", vuln: "#A5300F",
  email: "#8A5A2B", identity: "#6D4AA6", cloud: "#0E7490", aisec: "#B45309",
  siem: "#1F6F5C", itsm: "#9A3412",
};
const themeFor = (cat) => CATEGORY_THEME[cat] || "var(--cv-crimson)";

const relTime = (iso) => {
  if (!iso) return "never";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function Connectors() {
  const [catalog, setCatalog] = useState({ categories: [], connectors: [], cadence_options: [] });
  const [instances, setInstances] = useState([]);
  const [dialog, setDialog] = useState({ open: false, spec: null, instance: null });
  const [events, setEvents] = useState({ open: false, instance: null });
  const [busyId, setBusyId] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");

  const load = useCallback(async () => {
    const [cat, inst] = await Promise.all([
      apiGet("/connectors/catalog"),
      apiGet("/connectors/instances"),
    ]);
    setCatalog(cat);
    setInstances(inst.instances || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const specByKey = useMemo(() => Object.fromEntries(catalog.connectors.map((c) => [c.key, c])), [catalog]);
  const catLabel = useMemo(() => Object.fromEntries(catalog.categories.map((c) => [c.key, c.label])), [catalog]);

  const openStore = (spec) => setDialog({ open: true, spec, instance: null });
  const openEdit = (inst) => setDialog({ open: true, spec: specByKey[inst.connector_key], instance: inst });

  const testInstance = async (inst) => {
    setBusyId(inst.id);
    try { await apiPost(`/connectors/instances/${inst.id}/test`); await load(); }
    finally { setBusyId(null); }
  };
  const removeInstance = async (inst) => {
    if (!window.confirm(`Remove connector "${inst.name}"?`)) return;
    await apiDelete(`/connectors/instances/${inst.id}`); load();
  };

  const runHealth = async () => {
    setSweeping(true);
    try { await apiPost("/connectors/health/run"); await load(); }
    finally { setSweeping(false); }
  };

  const setCadence = async (inst, value) => {
    setInstances((list) => list.map((i) => (i.id === inst.id ? { ...i, check_interval_sec: value } : i)));
    await apiPatch(`/connectors/instances/${inst.id}`, { check_interval_sec: value });
    load();
  };

  const grouped = useMemo(() => {
    const g = {};
    catalog.connectors.forEach((c) => { (g[c.category] = g[c.category] || []).push(c); });
    return g;
  }, [catalog]);

  // Store search + category filter.
  const q = query.trim().toLowerCase();
  const visibleCats = useMemo(
    () => catalog.categories.filter((cat) => activeCat === "all" || cat.key === activeCat),
    [catalog.categories, activeCat],
  );
  const matches = (c) => !q || c.name.toLowerCase().includes(q) || c.vendor.toLowerCase().includes(q);
  const storeCount = useMemo(
    () => catalog.connectors.filter((c) => (activeCat === "all" || c.category === activeCat) && matches(c)).length,
    [catalog.connectors, activeCat, q],
  );

  return (
    <>
      <Masthead
        eyebrow="Integrations"
        title="Connector Store"
        subtitle="Connect your security tools — credentials are encrypted and tested against the live vendor API"
        meta={<span><strong>{catalog.connectors.length}</strong> connectors · <strong>{instances.length}</strong> configured</span>}
      />
      <div className="ciso flex-1" style={{ padding: "22px 26px 48px" }}>

        {/* -------- Configured -------- */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[16px] font-bold small-caps" style={{ color: "var(--cv-charcoal)" }}>Configured connectors</h3>
          {instances.length > 0 && (
            <Button variant="outline" size="sm" onClick={runHealth} disabled={sweeping} data-testid="run-health-btn">
              <ShieldCheck className={`w-3.5 h-3.5 mr-1 ${sweeping ? "animate-spin" : ""}`} /> {sweeping ? "Checking…" : "Run health check"}
            </Button>
          )}
        </div>
        {instances.length === 0 ? (
          <div className="ciso-panel text-[13px]" style={{ color: "var(--cv-dark-grey)" }} data-testid="no-connectors">
            No connectors configured yet. Pick one from the store below to get started.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="configured-list">
            {instances.map((inst) => {
              const spec = specByKey[inst.connector_key] || {};
              const s = STATUS_UI[inst.status] || STATUS_UI.untested;
              const S = s.Icon;
              return (
                <div key={inst.id} className="ciso-stat" data-testid={`inst-${inst.id}`} style={{ cursor: "default" }}>
                  <div className="ciso-stat__bar" style={{ background: inst.status === "connected" ? "var(--success-500)" : inst.status === "error" ? "var(--critical-600)" : "var(--tan)" }} />
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15, color: "var(--ink)", fontVariant: "small-caps" }}>{inst.name}</div>
                      <div className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>{spec.vendor} · {catLabel[spec.category] || spec.category}</div>
                    </div>
                    <span className="flex items-center gap-1 text-[11px] font-bold" style={{ background: s.bg, color: s.fg, borderRadius: 999, padding: "2px 9px" }}>
                      <S className="w-3 h-3" /> {s.label}
                    </span>
                  </div>
                  {inst.dropped && (
                    <div className="flex items-center gap-1.5 text-[11.5px] mt-2 font-bold" data-testid={`dropped-${inst.id}`}
                      style={{ background: "rgba(165,48,15,.10)", color: "#A5300F", borderRadius: 8, padding: "5px 9px" }}>
                      <AlertTriangle className="w-3.5 h-3.5" /> Connection dropped since last check
                    </div>
                  )}
                  {inst.last_result && (
                    <div className="text-[12px] mt-2" style={{ color: "var(--text-muted)" }}>{inst.last_result.message}</div>
                  )}
                  <div className="flex items-center gap-1.5 text-[11.5px] mt-1.5" style={{ color: "var(--text-muted)" }} data-testid={`synctime-${inst.id}`}>
                    <Clock className="w-3 h-3" /> Last checked {relTime(inst.last_tested_at)}
                  </div>
                  <div className="flex items-center gap-1.5 mt-2.5" data-testid={`cadence-row-${inst.id}`}>
                    <Timer className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />
                    <span className="text-[11.5px]" style={{ color: "var(--text-muted)" }}>Auto-check</span>
                    <select
                      value={inst.check_interval_sec || 0}
                      onChange={(e) => setCadence(inst, Number(e.target.value))}
                      data-testid={`cadence-${inst.id}`}
                      className="text-[11.5px] font-bold rounded px-1.5 py-1 outline-none cursor-pointer"
                      style={{ border: "1px solid var(--divider)", background: "var(--surface)", color: "var(--ink)" }}
                    >
                      {(catalog.cadence_options || []).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 mt-3" style={{ borderTop: "1px solid var(--divider)", paddingTop: 12 }}>
                    <Button variant="outline" size="sm" onClick={() => testInstance(inst)} disabled={busyId === inst.id} data-testid={`test-${inst.id}`}>
                      <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busyId === inst.id ? "animate-spin" : ""}`} /> Test
                    </Button>
                    <button className="p-1.5" title="Activity history" onClick={() => setEvents({ open: true, instance: inst })} data-testid={`history-${inst.id}`}><History className="w-4 h-4" /></button>
                    <button className="p-1.5" title="Edit" onClick={() => openEdit(inst)} data-testid={`edit-${inst.id}`}><Pencil className="w-4 h-4" /></button>
                    <button className="p-1.5" title="Remove" onClick={() => removeInstance(inst)} style={{ color: "var(--cv-crimson)" }} data-testid={`del-${inst.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* -------- Store -------- */}
        <h3 className="text-[16px] font-bold small-caps mt-8 mb-3" style={{ color: "var(--cv-charcoal)" }}>Connector store</h3>

        {/* search + category filter */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-[360px]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search connectors or vendors…"
              data-testid="store-search"
              className="w-full text-[13px] rounded pl-8 pr-8 py-2 outline-none"
              style={{ border: "1px solid var(--divider)", background: "var(--surface)", color: "var(--ink)" }}
            />
            {query && (
              <button onClick={() => setQuery("")} data-testid="store-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }}>
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="text-[11.5px] font-bold" style={{ color: "var(--text-muted)" }} data-testid="store-result-count">
            {storeCount} connector{storeCount === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-6" data-testid="store-category-filter">
          <button onClick={() => setActiveCat("all")} data-testid="cat-chip-all"
            className="text-[11.5px] font-bold rounded-full px-3 py-1.5 transition-colors"
            style={activeCat === "all"
              ? { background: "var(--cv-charcoal)", color: "#fff" }
              : { background: "var(--cv-sand)", color: "var(--cv-dark-grey)", border: "1px solid var(--divider)" }}>
            All
          </button>
          {catalog.categories.map((cat) => {
            const on = activeCat === cat.key;
            const theme = themeFor(cat.key);
            return (
              <button key={cat.key} onClick={() => setActiveCat(cat.key)} data-testid={`cat-chip-${cat.key}`}
                className="flex items-center gap-1.5 text-[11.5px] font-bold rounded-full px-3 py-1.5 transition-colors"
                style={on
                  ? { background: theme, color: "#fff" }
                  : { background: `${theme}12`, color: theme, border: `1px solid ${theme}33` }}>
                <span style={{ width: 7, height: 7, borderRadius: 2, background: on ? "#fff" : theme }} />
                {cat.label}
              </button>
            );
          })}
        </div>

        {storeCount === 0 && (
          <div className="ciso-panel text-[13px]" style={{ color: "var(--cv-dark-grey)" }} data-testid="store-empty">
            No connectors match your search.
          </div>
        )}
        {visibleCats.map((cat) => {
          const items = (grouped[cat.key] || []).filter(matches);
          if (items.length === 0) return null;
          return (
          <div key={cat.key} className="mb-6" data-testid={`store-cat-${cat.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <span style={{ width: 9, height: 9, borderRadius: 3, background: themeFor(cat.key) }} />
              <div className="text-[12px] font-bold ciso-cap" style={{ color: "var(--cv-dark-grey)" }}>{cat.label}</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {items.map((c) => {
                const theme = themeFor(cat.key);
                const configured = instances.find((i) => i.connector_key === c.key);
                const cs = configured ? (STATUS_UI[configured.status] || STATUS_UI.untested) : null;
                return (
                <div key={c.key} className="ciso-panel flex flex-col relative overflow-hidden" data-testid={`store-${c.key}`} style={{ padding: 16, borderTop: `3px solid ${theme}` }}>
                  <div className="flex items-start gap-2.5">
                    <span className="ciso-chip" style={{ background: `${theme}18`, color: theme }}><Plug className="w-[18px] h-[18px]" /></span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span style={{ fontWeight: 700, fontSize: 13.5, color: "var(--ink)" }}>{c.name}</span>
                        {c.verified && <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#2F6B41" }} title="Verified live" />}
                      </div>
                      <div className="text-[11.5px]" style={{ color: "var(--text-muted)" }}>{c.vendor}</div>
                    </div>
                    {cs && (
                      <span className="flex items-center gap-1 text-[10px] font-bold" data-testid={`store-status-${c.key}`}
                        style={{ background: cs.bg, color: cs.fg, borderRadius: 999, padding: "2px 7px" }}>
                        <cs.Icon className="w-2.5 h-2.5" /> {cs.label}
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] mt-2 flex-1" style={{ color: "var(--text-secondary)" }}>{c.description}</p>
                  <Button size="sm" className="mt-3 w-full" onClick={() => openStore(c)} data-testid={`connect-${c.key}`}
                    style={{ background: theme, color: "#fff" }}>
                    <PlugZap className="w-3.5 h-3.5 mr-1" /> {configured ? "Reconfigure" : "Connect"}
                  </Button>
                </div>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>

      <ConnectorDialog
        open={dialog.open}
        spec={dialog.spec}
        instance={dialog.instance}
        onOpenChange={(v) => setDialog((d) => ({ ...d, open: v }))}
        onSaved={load}
      />

      <EventsDialog
        open={events.open}
        instance={events.instance}
        onOpenChange={(v) => setEvents((e) => ({ ...e, open: v }))}
      />
    </>
  );
}
